import { useCallback, useMemo } from 'react';
import { useCollectionsStore } from '../store/collectionsStore';
import { fetchAndParseSpec, summarizeImport } from '../services/ai/importSpec';
import { useVariablesStore, setEnvironments, setSelectedEnvironment } from '../store/variablesStore';
import { useTabsStore } from '../store/tabsStore';
import { buildRequestParts, CODE_GENERATORS } from '../utils/codeGenerators';
import {
  findSidebarRequest,
  findParentCollectionId,
  findRequestTabForRequest,
  mergeRequestSources,
} from '../utils/requestResolve';
import { looksSecret } from '../services/ai/secretHeuristics';
import { mockPathFromUrl, buildMockUrl } from '../services/mockServerService';
import { normalizeMock, REQUEST_KINDS } from '../utils/workspaceTabPersistence';
import { useMockServerStore } from '../store/mockServerStore';
import { useWorkspaceStore } from '../store/workspaceStore';
import { folderVariablesForRequest } from '../utils/folderVariables';
import { collectionAuthOf } from '../utils/requestAssembly';
import { folderAuthChainForRequest } from '../utils/requestFolderAuth';
import { scopeRowsLowestFirst } from '../utils/variablePrecedence';
import { gitTrackingReport } from '../services/git/gitTracking';
import { readOnlyWorkspaceRefusal } from '../plans/capRefusal';
import { apiCapRefusal } from '../plans/localApiUsage';

/**
 * AI executors for history, environment lifecycle and auth (18.08.2026).
 *
 * These close three gaps the 17.08 audit found: the request history was
 * invisible to the assistant even though "what failed an hour ago?" is the
 * most natural debugging question in an API client; environments were
 * read-only; and auth could not be configured at all — the step immediately
 * after every import.
 *
 * A separate hook from useAiActions on purpose: that file is already large and
 * these share no state with it beyond the stores they read.
 */

const HISTORY_DEFAULT_LIMIT = 25;
const HISTORY_MAX_LIMIT = 100;

/** Match an environment by id, else by case-insensitive name. */
function findEnvironment(environments, { environment_id: id, environment_name: name }) {
  const list = environments || [];
  if (id) {
    const byId = list.find((e) => String(e.id) === String(id));
    if (byId) return byId;
  }
  if (name) {
    const target = String(name).trim().toLowerCase();
    return list.find((e) => String(e.name || '').trim().toLowerCase() === target) || null;
  }
  return null;
}

const genId = () => `env_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

/** Language id -> the library the generated snippet uses, for the summary. */
const CODEGEN_LIBRARIES = {
  curl: 'Shell',
  python: 'Requests',
  typescript: 'Fetch',
  go: 'net/http',
  java: 'HttpClient',
  csharp: 'HttpClient',
  php: 'cURL',
  ruby: 'Net::HTTP',
  swift: 'URLSession',
};

/**
 * Variables for a generated snippet, with credentials forced to redact.
 *
 * `buildRedactingSubstitute` honours `v.secret`, which is the user\'s explicit
 * mark. That is the right default for the modal, where a human is looking at
 * the output. On the AI path the snippet also lands in the chat transcript and
 * goes to a third-party model provider, so the explicit flag alone is too
 * narrow: a token pasted into an ordinary variable has `secret:false` and
 * would be substituted verbatim. `looksSecret` is the same heuristic
 * `get_environments` already applies for exactly this reason.
 */
function guardCodegenVariables(variables) {
  // Falsy rows are dropped, not passed through: buildRedactingSubstitute reads
  // `v.enabled` unguarded, so one malformed row in an imported environment
  // would turn every generate_code call into an opaque failure.
  return (variables || [])
    .filter(Boolean)
    .map((v) => (v.secret || looksSecret(v.key, v.value) ? { ...v, secret: true } : v));
}

/** The request a request-scoped tool call refers to, plus its collection. */
function resolveRequestTarget(input, readLiveDraft) {
  const { collections } = useCollectionsStore.getState();
  const { requestTabs, activeRequestTabId, activeTabType } = useTabsStore.getState();

  if (input.use_current_tab) {
    if (activeTabType !== 'request') return { error: 'No active request tab' };
    const tab = (requestTabs || []).find((t) => String(t.id) === String(activeRequestTabId));
    if (!tab) return { error: 'No active request tab' };
    // The live draft is what is on screen; the tab record can lag it by an
    // edit. Generating from the stale one produces a snippet that does not
    // match what the user is looking at.
    // `hasUnsavedChanges: true` is what makes mergeRequestSources prefer the
    // draft — it is the flag the merge switches on, and a draft read straight
    // off the editor does not carry it. resolveRequestRecord forces it the
    // same way for the same reason.
    const draft = readLiveDraft?.() || null;
    const request = draft ? mergeRequestSources(tab, { ...draft, hasUnsavedChanges: true }) : tab;
    const collectionId = tab.collectionId
      ?? (tab.originalId ? findParentCollectionId(collections, tab.originalId) : null);
    return { request, collectionId, name: request.name || tab.name || null };
  }

  const requestId = String(input.request_id || '');
  if (!requestId) return { error: 'request_id is required, or set use_current_tab' };

  const sidebar = findSidebarRequest(collections, requestId);
  if (!sidebar) return { error: 'Request not found' };
  const tab = findRequestTabForRequest(requestTabs, requestId, activeRequestTabId);
  let request = mergeRequestSources(sidebar, tab);
  // Only the ACTIVE tab has a live draft to merge; another open tab\'s record
  // is already the latest state it has.
  if (tab && String(tab.id) === String(activeRequestTabId)) {
    const draft = readLiveDraft?.() || null;
    if (draft) request = mergeRequestSources(request, { ...draft, hasUnsavedChanges: true });
  }
  return {
    request,
    collectionId: findParentCollectionId(collections, requestId),
    name: request.name || null,
  };
}

/** Content types a mock example may declare. Enumerated so every provider's
 *  schema subset carries it intact — an open string invites nonsense. */
const MOCK_CONTENT_TYPES = new Set([
  'application/json', 'application/xml', 'text/plain', 'text/html',
  'application/javascript', 'application/octet-stream',
]);

/** `a=1&b=2` → the {key,value} rows the mock server expects. */
function parseMatchPairs(text) {
  if (!text || typeof text !== 'string') return null;
  const rows = text
    .replace(/^[?&]+/, '')
    .split('&')
    .map((pair) => {
      const eq = pair.indexOf('=');
      if (eq === -1) return null;
      const key = pair.slice(0, eq).trim();
      return key ? { key, value: pair.slice(eq + 1).trim() } : null;
    })
    .filter(Boolean);
  return rows.length ? rows : null;
}

/** The captured response for a request, from whichever tab holds it. */
function capturedResponseFor(requestId, activeOnly) {
  const { requestTabs, activeRequestTabId } = useTabsStore.getState();
  const tabs = requestTabs || [];
  const tab = activeOnly
    ? tabs.find((t) => String(t.id) === String(activeRequestTabId))
    : tabs.find((t) => String(t.originalId ?? t.id) === String(requestId));
  return tab?.response || null;
}

/** A response body as the string a mock will serve. */
function responseBodyText(response) {
  const data = response?.data;
  if (typeof data === 'string') return data;
  if (data === null || data === undefined) return '';
  try {
    return JSON.stringify(data, null, 2);
  } catch {
    return String(data);
  }
}

export function useAiWorkspaceActions({
  onUpdateRequest,
  onCreateRequest,
  onImportCollections,
  readLiveRequestState,
} = {}) {
  /**
   * Read the request history.
   *
   * Returns metadata only — method, url, status, timing. Bodies are
   * deliberately excluded: history holds up to 100 entries and forwarding
   * their bodies would ship far more third-party response data to the model
   * than any question needs. `get_last_response` exists for the one body the
   * user is actually asking about, and it is gated.
   */
  const aiGetHistory = useCallback((input = {}) => {
    const all = useCollectionsStore.getState().history || [];
    const {
      only_failures: onlyFailures,
      status_min: statusMin,
      status_max: statusMax,
      url_contains: urlContains,
      since_minutes: sinceMinutes,
    } = input;

    const cutoff = Number.isFinite(Number(sinceMinutes)) && Number(sinceMinutes) > 0
      ? Date.now() - Number(sinceMinutes) * 60_000
      : null;
    const needle = urlContains ? String(urlContains).toLowerCase() : null;

    const matched = all.filter((h) => {
      const status = Number(h.status ?? 0);
      // status 0 is a transport failure — the user means it when they say
      // "what failed", so it counts.
      if (onlyFailures && !(status === 0 || status >= 400)) return false;
      if (Number.isFinite(Number(statusMin)) && status < Number(statusMin)) return false;
      if (Number.isFinite(Number(statusMax)) && status > Number(statusMax)) return false;
      if (needle && !String(h.url || '').toLowerCase().includes(needle)) return false;
      if (cutoff && Number(h.timestamp || 0) < cutoff) return false;
      return true;
    });

    const limit = Math.min(
      Math.max(1, Number(input.limit) || HISTORY_DEFAULT_LIMIT),
      HISTORY_MAX_LIMIT
    );

    return {
      total_matched: matched.length,
      entries: matched.slice(0, limit).map((h) => ({
        // NOT a request id: useCollections.js stamps every entry with its own
        // `hist_…` id and that overwrites any request id in the spread. Naming
        // it request_id sent the model to get_request with a value that can
        // never resolve. `requestKey` is the saved request when there is one.
        history_id: h.id,
        request_id: h.requestKey ? String(h.requestKey) : null,
        name: h.name || null,
        method: h.method || 'GET',
        url: h.url || '',
        status: h.status ?? 0,
        response_time_ms: h.responseTime ?? null,
        timestamp: h.timestamp || null,
      })),
      entries_omitted: Math.max(0, matched.length - limit) || undefined,
    };
  }, []);

  const aiCreateEnvironment = useCallback(async (input = {}) => {
    const name = String(input.name || '').trim();
    if (!name) return { error: 'name is required' };

    const capped =
      readOnlyWorkspaceRefusal(useWorkspaceStore.getState().currentWorkspace) || (await apiCapRefusal('environments'));
    if (capped) return { error: capped };
    const environments = useVariablesStore.getState().environments || [];
    if (environments.some((e) => String(e.name || '').trim().toLowerCase() === name.toLowerCase())) {
      return { error: `An environment named "${name}" already exists` };
    }

    const environment = {
      id: genId(),
      name,
      variables: (input.variables || [])
        .filter((v) => v && v.key)
        .map((v) => ({ key: String(v.key), value: String(v.value ?? ''), secret: Boolean(v.secret) })),
    };
    setEnvironments([...environments, environment]);

    return {
      success: true,
      environment_id: environment.id,
      name: environment.name,
      variable_count: environment.variables.length,
    };
  }, []);

  const aiDuplicateEnvironment = useCallback(async (input = {}) => {
    const newName = String(input.new_name || '').trim();
    if (!newName) return { error: 'new_name is required' };

    if (!findEnvironment(useVariablesStore.getState().environments || [], input)) return { error: 'Environment not found' };
    const capped =
      readOnlyWorkspaceRefusal(useWorkspaceStore.getState().currentWorkspace) || (await apiCapRefusal('environments'));
    if (capped) return { error: capped };
    const environments = useVariablesStore.getState().environments || [];
    const source = findEnvironment(environments, input);
    if (!source) return { error: 'Environment not found' };

    const copy = {
      // Values are copied verbatim, including secrets — but nothing here
      // returns them, and get_environments masks them on the way out.
      ...source,
      id: genId(),
      name: newName,
      variables: (source.variables || []).map((v) => ({ ...v })),
    };
    setEnvironments([...environments, copy]);

    return {
      success: true,
      environment_id: copy.id,
      name: copy.name,
      copied_from: source.name,
      variable_count: copy.variables.length,
    };
  }, []);

  const aiSwitchEnvironment = useCallback((input = {}) => {
    const environments = useVariablesStore.getState().environments || [];
    const target = findEnvironment(environments, input);
    if (!target) return { error: 'Environment not found' };

    setSelectedEnvironment(target);
    return { success: true, environment_id: target.id, name: target.name };
  }, []);

  /**
   * Configure auth on a saved request.
   *
   * Routes through the existing update_request path so it inherits that
   * tool's Confirm card and persistence — auth is a saved-state mutation and
   * gets the same gate as any other.
   */
  const aiSetAuth = useCallback(async (input = {}) => {
    const requestId = String(input.request_id || '');
    if (!requestId) return { error: 'request_id is required' };
    if (!onUpdateRequest) return { error: 'Updating requests is not available' };

    const type = String(input.type || '');
    let auth;
    switch (type) {
      case 'bearer':
        if (!input.token) return { error: 'token is required for bearer auth' };
        auth = { type: 'bearer', bearer: String(input.token) };
        break;
      case 'basic':
        if (!input.username) return { error: 'username is required for basic auth' };
        auth = {
          type: 'basic',
          basic: { username: String(input.username), password: String(input.password ?? '') },
        };
        break;
      case 'apikey':
        if (!input.key) return { error: 'key is required for apikey auth' };
        auth = {
          type: 'apikey',
          apikey: {
            key: String(input.key),
            value: String(input.value ?? ''),
            in: input.add_to === 'query' ? 'query' : 'header',
          },
        };
        break;
      case 'noauth':
      case 'inherit':
        auth = { type };
        break;
      default:
        return { error: `Unsupported auth type: ${type || '(none)'}` };
    }

    const result = await onUpdateRequest({ request_id: requestId, auth });
    if (result?.error) return result;

    return {
      success: true,
      request_id: requestId,
      auth_type: type,
      // Never echo the credential back — the model does not need it and it
      // would land in the transcript.
      uses_variable: /\{\{.+\}\}/.test(
        String(input.token ?? input.password ?? input.value ?? '')
      ),
    };
  }, [onUpdateRequest]);

  /**
   * Import a spec into collections.
   *
   * Parsing and applying are the app's existing paths — this only joins them
   * and returns a summary. The parsed collections deliberately do NOT go back
   * to the model: a real OpenAPI document is thousands of endpoints, and the
   * user pays for every token of it.
   */
  const aiImportSpec = useCallback(async (input = {}) => {
    if (!onImportCollections) return { error: 'Importing is not available' };

    const { parsed, error } = await fetchAndParseSpec(input);
    if (error) return { error };
    if (parsed.kind === 'environment') {
      return { error: 'That file is a Postman environment, not a collection — import it via the Import dialog instead' };
    }
    if (!parsed.collections?.length) return { error: 'The spec contained no requests' };

    try {
      await onImportCollections(parsed.collections);
    } catch (e) {
      return { error: `Import failed: ${e?.message || String(e)}` };
    }

    return { success: true, ...summarizeImport(parsed) };
  }, [onImportCollections]);

  /**
   * Generate a code snippet for a request.
   *
   * Read-only: nothing is written, opened or sent. The whole risk surface is
   * what the snippet CONTAINS — see guardCodegenVariables. There is no
   * reveal-secrets option by design; if the user wants their real token in the
   * snippet, the Code Generator modal is where that choice belongs.
   */
  const aiGenerateCode = useCallback((input = {}) => {
    const language = String(input.language || '').trim().toLowerCase();
    const generator = CODE_GENERATORS.find((g) => g.id === language);
    if (!generator) {
      return {
        error: `Unsupported language: ${language || '(none)'}. Supported: ${CODE_GENERATORS.map((g) => g.id).join(', ')}`,
      };
    }

    const target = resolveRequestTarget(input, readLiveRequestState);
    if (target.error) return { error: target.error };
    if (!target.request?.url) return { error: 'That request has no URL yet' };

    const vars = useVariablesStore.getState();
    const collections = useCollectionsStore.getState().collections || [];
    const collection = collections.find((c) => String(c.id) === String(target.collectionId));
    // Where the collection's auth actually lives (`settings.collection_auth`),
    // and the folder chain, so `inherit` resolves as Send resolves it.
    const collectionAuth = collectionAuthOf(collection);
    const folderAuthChain = folderAuthChainForRequest(target.request, collection);

    // Order is precedence, LOWEST first — buildRedactingSubstitute fills a Map
    // in sequence, so the last entry for a key wins. Same order as the modal
    // and as a real request resolves in.
    const variables = guardCodegenVariables(scopeRowsLowestFirst({
      global: vars.globalVariables,
      workspace: vars.workspaceVariables,
      environment: vars.selectedEnvironment?.variables,
      collection: vars.collectionVariablesById?.[target.collectionId],
      folder: folderVariablesForRequest(target.request, collections, target.collectionId),
    }));

    let code;
    try {
      const parts = buildRequestParts(target.request, collectionAuth, variables, {
        revealSecrets: false,
        folderAuthChain,
      });
      code = generator.fn(parts, {});
    } catch (e) {
      return { error: `Could not generate the snippet: ${e?.message || String(e)}` };
    }

    return {
      language,
      library: CODEGEN_LIBRARIES[language] || null,
      request_name: target.name,
      method: (target.request.method || 'GET').toUpperCase(),
      code,
      // Stated rather than implied: the model has to know a placeholder is a
      // placeholder, or it will confidently tell the user to run a snippet
      // that 401s.
      secrets_redacted: code.includes('<SECRET_REDACTED>'),
    };
  }, [readLiveRequestState]);

  /**
   * Save a mock response example on a request.
   *
   * Delegates the WRITE to onUpdateRequest rather than touching stores itself:
   * `mock` already rides both save paths (cloud PUT and the local tree), and a
   * second persistence route for one field is how the two drift apart. Note
   * that delegating does NOT inherit update_request's confirm gate —
   * toolNeedsConfirm keys on the tool the model called — so this tool carries
   * its own row in TOOL_REGISTRY.
   *
   * The body never comes back in the result. For source:'last_response' the
   * copy happens in-process: the model asks for the mock, it does not read the
   * payload and hand it over. That keeps a live production response out of the
   * transcript and out of the provider's prompt.
   */
  const aiCreateMockExample = useCallback(async (input = {}) => {
    if (!onUpdateRequest) return { error: 'Updating requests is not available' };

    const target = resolveRequestTarget(input, readLiveRequestState);
    if (target.error) return { error: target.error };

    const requestId = String(target.request?.originalId ?? target.request?.id ?? '');
    if (!requestId) return { error: 'That request has not been saved yet — save it first' };
    // A mock route is built per collection request; buildMockDataset iterates
    // collections only, so a request outside one can never be served.
    if (!target.collectionId) return { error: 'That request is not in a collection, so it has no mock route' };
    const path = mockPathFromUrl(target.request?.url);
    if (!path) return { error: 'That request has no URL path to mock' };

    const source = input.source === 'literal' ? 'literal' : 'last_response';
    let body;
    let status = Number(input.status) || 0;
    let contentType = input.content_type;

    if (source === 'literal') {
      if (typeof input.body !== 'string') return { error: "body is required when source is 'literal'" };
      body = input.body;
    } else {
      const response = capturedResponseFor(requestId, Boolean(input.use_current_tab));
      if (!response) {
        return { error: 'No captured response for that request yet — send it once, then mock it' };
      }
      body = responseBodyText(response);
      if (!status) status = Number(response.status) || 200;
      if (!contentType) {
        const headers = response.headers || {};
        contentType = headers['content-type'] || headers['Content-Type'] || null;
      }
    }
    if (!status) status = 200;
    if (contentType) contentType = String(contentType).split(';')[0].trim();
    if (contentType && !MOCK_CONTENT_TYPES.has(contentType)) contentType = 'application/json';

    const existing = normalizeMock(target.request?.mock);
    const example = {
      id: `ex-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name: String(input.name || '').trim() || `${status} response`,
      status,
      contentType: contentType || 'application/json',
      headers: [],
      body,
      matchQuery: parseMatchPairs(input.match_query),
      matchHeaders: input.match_header_name
        ? [{ key: String(input.match_header_name), value: String(input.match_header_value ?? '') }]
        : null,
      enabled: true,
      sourceType: 'example',
      schemaId: null,
    };

    const responseExamples = [...(existing.responseExamples || []), example];
    const result = await onUpdateRequest({ request_id: requestId, mock: { ...existing, responseExamples } });
    if (result?.error) return result;

    const { running, baseUrl } = useMockServerStore.getState();
    // Two enabled examples with no conditions means the server picks between
    // them per request. That is a legitimate thing to want and a surprising
    // thing to get by accident, so it is reported rather than prevented.
    // `normalizeMock` fills absent conditions with a BLANK editor row, so
    // `matchQuery: [{key:'',value:''}]` means "no condition" — the serializer
    // drops keyless rows before they reach the server. Counting the array as a
    // condition would silently under-report the random-selection case.
    const hasCondition = (rows) => Array.isArray(rows) && rows.some((kv) => kv && kv.key);
    const unconditional = responseExamples.filter(
      (ex) => ex.enabled !== false && !hasCondition(ex.matchQuery) && !hasCondition(ex.matchHeaders)
    );

    return {
      success: true,
      request_id: requestId,
      example_id: example.id,
      name: example.name,
      status,
      content_type: example.contentType,
      // A count, never the payload.
      body_bytes: body.length,
      route_path: path,
      mock_url: running ? buildMockUrl(baseUrl, target.collectionId, path) : null,
      mock_server_running: Boolean(running),
      example_count: responseExamples.length,
      random_between: unconditional.length > 1 ? unconditional.length : undefined,
      copied_from_live_response: source === 'last_response',
    };
  }, [onUpdateRequest, readLiveRequestState]);

  /**
   * Report the workspace's Git sync state.
   *
   * Read-only and deliberately the only Git tool for now. Pushing a workspace
   * to a remote is an outward-facing act on someone else's server, and the
   * existing push path (`handleGitSync`) returns nothing a tool could report
   * back — a "pushed successfully" built on that would be a guess. That needs
   * its own decision, not a default.
   *
   * Nothing here can carry a credential: `git_status` is one of the Rust
   * commands that takes no token at all, and the fields below are counts,
   * booleans and the repo name the user chose.
   */
  const aiGetGitStatus = useCallback(async () => {
    const workspace = useWorkspaceStore.getState().currentWorkspace;
    if (!workspace) return { error: 'No workspace is open' };
    if (!workspace.git) {
      return {
        connected: false,
        workspace_name: workspace.name || null,
        note: 'This workspace is not connected to a Git repository',
        tracking: gitTrackingReport(workspace),
      };
    }

    let status;
    try {
      // Imported lazily on purpose. workspaceGitSync reaches @tauri-apps/api
      // transitively, and a static import here would drag the Tauri runtime
      // into every consumer of this hook — including the web build, where Git
      // does not exist, and every test that touches any other tool in it.
      const { gitStatus } = await import('../services/workspaceGitSync');
      status = await gitStatus(workspace);
    } catch (e) {
      // Offline, no mirror on disk yet, or a repo that was moved. The model
      // should say so rather than report a clean tree it never saw.
      return { error: `Could not read Git status: ${e?.message || String(e)}` };
    }

    const { owner, repo, branch } = workspace.git;
    // Tracked only on this device (Track on this device): no repository to name.
    if (workspace.git.provider === 'local') {
      return {
        connected: Boolean(status?.initialized ?? true),
        local_only: true,
        workspace_name: workspace.name || null,
        repo: null,
        branch: branch || null,
        has_local_changes: Boolean(status?.hasLocalChanges),
        ahead: 0,
        behind: 0,
        tracking: gitTrackingReport(workspace),
      };
    }
    return {
      // git_status serializes camelCase (Rust `rename_all = "camelCase"`); the
      // snake_case reads here were always undefined — "no local changes" every time.
      connected: Boolean(status?.initialized ?? status?.hasRemote ?? true),
      workspace_name: workspace.name || null,
      repo: owner && repo ? `${owner}/${repo}` : null,
      branch: branch || null,
      has_local_changes: Boolean(status?.hasLocalChanges),
      ahead: Number(status?.ahead ?? 0),
      behind: Number(status?.behind ?? 0),
      // The same four facts MCP's workspace.git reports (tracked, kind,
      // provider, changesWaiting) — services/git/gitTracking.js.
      tracking: gitTrackingReport(workspace),
    };
  }, []);

  /**
   * Create a saved WebSocket / SSE / gRPC connection.
   *
   * Deliberately "save a connection", not "hold one open". A live socket needs
   * a lifecycle the tool protocol has nowhere to put — nothing owns the close,
   * and a turn that ends leaves it dangling. Saving one gives the user
   * something they can open, inspect and run themselves, which is what "set up
   * a websocket for this endpoint" actually means.
   *
   * Delegates to onCreateRequest so the connection is a request like any other
   * — same persistence, same sidebar, same editor. The only thing that makes it
   * a stream is `type`, which is why this could not ship until `type` survived
   * being saved.
   */
  const aiCreateStreamConnection = useCallback(async (input = {}) => {
    if (!onCreateRequest) return { error: 'Creating requests is not available' };

    const kind = String(input.kind || '').toLowerCase();
    if (!REQUEST_KINDS.includes(kind) || kind === 'http') {
      return { error: `kind must be one of: ${REQUEST_KINDS.filter((k) => k !== 'http').join(', ')}` };
    }

    const url = String(input.url || '').trim();
    if (!url) return { error: 'url is required' };
    // A wrong scheme is the single most common way one of these silently fails
    // to connect, and the model has no way to find that out afterwards.
    if (kind === 'websocket' && !/^wss?:\/\//i.test(url)) {
      return { error: 'A WebSocket url must start with ws:// or wss://' };
    }
    if (kind === 'sse' && !/^https?:\/\//i.test(url)) {
      return { error: 'An SSE url must start with http:// or https://' };
    }

    const name = String(input.name || '').trim() || url;
    const result = await onCreateRequest({
      name,
      method: 'GET',
      url,
      collection_id: input.collection_id,
      folder_id: input.folder_id,
      type: kind,
    });
    if (result?.error) return result;

    return {
      success: true,
      request_id: result?.request?.id ?? result?.request_id ?? null,
      name,
      kind,
      url,
      // Saved, not connected — say so, or the model will report a live stream.
      connected: false,
      note: 'Saved as a connection. Open it to connect — this tool does not hold a live stream.',
    };
  }, [onCreateRequest]);

  return useMemo(() => ({
    import_spec: aiImportSpec,
    get_history: aiGetHistory,
    create_environment: aiCreateEnvironment,
    duplicate_environment: aiDuplicateEnvironment,
    switch_environment: aiSwitchEnvironment,
    set_auth: aiSetAuth,
    generate_code: aiGenerateCode,
    create_mock_example: aiCreateMockExample,
    get_git_status: aiGetGitStatus,
    create_stream_connection: aiCreateStreamConnection,
  }), [aiImportSpec, aiGetHistory, aiCreateEnvironment, aiDuplicateEnvironment, aiSwitchEnvironment, aiSetAuth, aiGenerateCode, aiCreateMockExample, aiGetGitStatus, aiCreateStreamConnection]);
}
